﻿import { createFileRoute } from "@tanstack/react-router";
import type { JSX } from "react";

import { COUNCIL_STEP_ID, enterCouncilSubStep } from "@/components/flows/steps/council";
import { FLOW_SUB_STEP_COMPONENTS } from "@/components/flows/steps/flow-sub-step-components";
import { STEP_COMPONENTS } from "@/components/flows/steps/flow-step-components";
import { useFlowStepContext } from "@/lib/context/flow-step-context";
import type { UserContext } from "@/lib/interfaces/user-context";

interface FlowSubStepEntryArgs {
  context: { user: Pick<UserContext, "fluxosPendentes"> };
  params: { stepId: string; subStepId: string };
}

/**
 * Preparação de entrada dos sub-steps, antes de a tela montar.
 *
 * Esta rota é genérica, então cada step que precisa preparar algo — reconstruir rascunho, barrar
 * sub-step inalcançável — exporta a própria função e é chamada aqui, sempre atrás da conferência
 * de que a URL é a dele.
 */
const enterFlowSubStep = ({ context, params }: FlowSubStepEntryArgs): void => {
  if (Number(params.stepId) === COUNCIL_STEP_ID) {
    enterCouncilSubStep({ context, params });
  }
};

const FlowSubStepPage = (): JSX.Element | null => {
  const { step, nextStep } = useFlowStepContext();
  const { subStepId } = Route.useParams();

  // Steps com múltiplos sub-steps: usa o mapa de sub-steps
  const subStepMap = FLOW_SUB_STEP_COMPONENTS[step.codEtapa];
  const SubStepComponent = subStepMap?.[subStepId];

  if (SubStepComponent) {
    return <SubStepComponent />;
  }

  // Steps simples (sub-step "main"): usa o componente de step
  const StepComponent = STEP_COMPONENTS[step.codEtapa];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Is not always false
  if (!StepComponent) {
    void nextStep();
    return null;
  }

  return <StepComponent />;
};

export const Route = createFileRoute("/flows/$stepId/$subStepId")({
  component: FlowSubStepPage,
  beforeLoad: enterFlowSubStep,
});
